import { Badge, Checkbox } from '@@/ui/primitives';
import { DefaultValue, Field, SchemaFieldType, Value } from '../../schemas/entity';
import {
  DateTimeInput,
  PickListInput,
  StringInput,
  NumberInput,
  BooleanInput,
  PromptInput,
  ReferenceInput,
  ConnectResourceInput
} from '../shared/field-inputs';
import clsx from 'clsx';
import { ObjectInput } from '../shared/field-inputs/ObjectInput';
import { FieldViewer } from '../shared/field-viewers';
import { ConnectResourceType, EntityRecord } from '../../api/api-entities';
import { cn } from '@@/lib/utils';

import { ConnectResourceValue, isConnectResourceValue } from '../../common/connect-resource';

type DateTimeValue = { timeZone: string; date: string; time: string };
type PromptValue = { content: string; kind: string };

const isDateTimeValue = (value: unknown): value is DateTimeValue =>
  typeof value === 'object' && value !== null && 'timeZone' in value;

const isPromptValue = (value: unknown): value is PromptValue =>
  typeof value === 'object' && value !== null && 'content' in value;

const isConnectResourceInput = (value: unknown): value is ConnectResourceValue => isConnectResourceValue(value);

interface FieldEditorProps {
  entity: EntityRecord;
  fieldName: string;
  fieldType: SchemaFieldType;
  field: Field;
  currentValue: DefaultValue;
  hasParent: boolean;
  editValue?: {
    value?: Value | null;
    overridable: boolean;
    source: 'user' | 'inherited';
    modifiable: boolean;
  };
  isEditing: boolean;
  onValueChange: (fieldName: string, value: Value | null) => void;
  onOverridableChange: (fieldName: string, overridable: boolean) => void;
  onSourceChange: (fieldName: string, source: 'user' | 'inherited') => void;
  onCancel: () => void;
  onAdd?: (fieldName: string) => void;
  onClear?: (fieldName: string) => void;
  onEdit?: (fieldName: string) => void;
}

const getInputForType = (
  entity: EntityRecord,
  fieldType: SchemaFieldType,
  field: Field,
  value: Value | null,
  onChange: (value: Value | null) => void
) => {
  switch (fieldType) {
    case SchemaFieldType.BOOLEAN:
      return <BooleanInput value={Boolean(value)} onChange={onChange} />;
    case SchemaFieldType.NUMBER:
      return (
        <NumberInput
          value={typeof value === 'number' ? value : 0}
          onChange={onChange}
          min={field.definition.type === SchemaFieldType.NUMBER ? field.definition.minimum : undefined}
          max={field.definition.type === SchemaFieldType.NUMBER ? field.definition.maximum : undefined}
        />
      );
    case SchemaFieldType.DATE_TIME:
      return (
        <DateTimeInput
          value={isDateTimeValue(value) ? value : { timeZone: 'UTC', date: '', time: '' }}
          onChange={onChange as (value: DateTimeValue) => void}
        />
      );
    case SchemaFieldType.PICK_LIST:
      return (
        <PickListInput
          options={field.definition.type === SchemaFieldType.PICK_LIST ? field.definition.options : []}
          multiple={field.definition.type === SchemaFieldType.PICK_LIST ? field.definition.multiple : false}
          value={typeof value === 'string' ? value : ''}
          onChange={onChange}
          required={field.definition.required}
        />
      );
    case SchemaFieldType.PROMPT:
      return (
        <PromptInput
          value={isPromptValue(value) ? value : { content: '', kind: 'SSML' }}
          onChange={onChange as (value: PromptValue) => void}
        />
      );
    case SchemaFieldType.REFERENCE:
      if (field.definition.type !== SchemaFieldType.REFERENCE) {
        throw new Error('Field definition type mismatch for REFERENCE');
      }

      return (
        <ReferenceInput
          baseEntityId={field.definition.entityId}
          value={value as { ref: { eid: string; iid: string } }}
          onChange={onChange as (value: { ref: { eid: string; iid: string } }) => void}
        />
      );
    case SchemaFieldType.OBJECT:
      return <ObjectInput value={value as string} onChange={onChange as (value: string) => void} />;
    case SchemaFieldType.CONNECT_RESOURCE:
      return (
        <ConnectResourceInput
          resourceType={
            field.definition.type === SchemaFieldType.CONNECT_RESOURCE
              ? field.definition.resourceType
              : ConnectResourceType.QUEUE
          }
          value={isConnectResourceInput(value) ? value : null}
          onChange={onChange as unknown as (value: ConnectResourceValue) => void}
        />
      );
    case SchemaFieldType.STRING:
    default:
      return <StringInput value={typeof value === 'string' ? value : ''} onChange={onChange} />;
  }
};

export const FieldEditor = ({
  entity,
  fieldName,
  fieldType,
  field,
  currentValue,
  hasParent,
  editValue,
  isEditing,
  onValueChange,
  onOverridableChange,
  onSourceChange,
  onAdd,
  onEdit
}: FieldEditorProps) => {
  const displayValue = editValue?.value ?? currentValue?.value;
  const displayOverridable = editValue?.overridable ?? currentValue?.overridable ?? true;
  const displaySource = editValue?.source ?? currentValue?.source ?? 'user';
  const isInherited = displaySource === 'inherited';

  return (
    <div className={cn(isEditing ? 'py-2 rounded-lg ring-1 ring-inset ring-slate-300' : '')}>
      <div className='flex items-center gap-2 px-4 h-6'>
        <span className={`text-sm font-medium text-slate-950`}>{fieldName}</span>
        <div className='flex items-center gap-2'>
          {hasParent && currentValue && (
            <>
              <Badge variant='outline' className='text-xs text-slate-950 border-slate-300 py-0.5'>
                {(editValue?.source ?? currentValue.source) === 'user' ? 'User' : 'Inherited'}
              </Badge>
            </>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className='px-4 py-2 flex flex-col gap-2'>
          {/* Inherit Toggle */}
          {hasParent && (
            <div className='flex items-center gap-2 text-sm'>
              {/* <Switch
                checked={isInherited}
                onCheckedChange={(checked) => onSourceChange(fieldName, checked ? 'inherited' : 'user')}
              /> */}
              <Checkbox
                checked={isInherited}
                onCheckedChange={(checked) => onSourceChange(fieldName, checked ? 'inherited' : 'user')}
              />
              <span className='text-slate-950'>Inherit</span>
            </div>
          )}

          {!isInherited &&
            getInputForType(entity, fieldType, field, displayValue, (value) => onValueChange(fieldName, value))}

          <div className='flex flex-col items-start mt-auto gap-4'>
            <div className='flex items-center justify-between w-full'>
              <div className='flex items-center gap-2 text-sm'>
                <Checkbox
                  checked={displayOverridable}
                  onCheckedChange={(checked) => onOverridableChange(fieldName, !!checked)}
                />
                <span className='text-slate-950'>Overrideable</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className='px-4 pt-1 pb-3 text-sm'>
          {currentValue.value !== null ? (
            <span
              className={clsx(
                'text-slate-950 cursor-pointer-mx-1  rounded',
                { 'cursor-not-allowed': !currentValue.modifiable },
                { ' hover:bg-slate-100 ': currentValue.modifiable }
              )}
              onClick={() => currentValue.modifiable && onEdit?.(fieldName)}
            >
              <FieldViewer value={currentValue.value} field={field} showFullContent={false} />
            </span>
          ) : (
            <span
              className={clsx(
                'text-slate-400 cursor-pointer hover:text-slate-600',
                { 'cursor-not-allowed': !currentValue.modifiable },
                { ' hover:bg-slate-100 ': currentValue.modifiable }
              )}
              onClick={() => currentValue.modifiable && onAdd?.(fieldName)}
            >
              + Add default value
            </span>
          )}
        </div>
      )}
    </div>
  );
};
